import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCreditCard, FiSmartphone, FiCheckCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import subscriptionService from '../services/subscriptionService';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import './Auth.css';

const Subscribe = () => {
  const navigate = useNavigate();
  const { refreshSubscription } = useAuth();
  const [config, setConfig] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [configRes, subRes] = await Promise.all([
        subscriptionService.getConfig(),
        subscriptionService.getSubscription().catch(() => ({ data: null })),
      ]);
      setConfig(configRes.data);
      setSubscription(subRes.data ?? {});
      await refreshSubscription();
    } catch (e) {
      toast.error('Failed to load subscription info');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await subscriptionService.requestPayment({
        transactionId: transactionId.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(
        "Payment request submitted. We'll activate your subscription within 24 hours. You can click 'Check status' after activation."
      );
      setTransactionId('');
      setNotes('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit payment request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="spinner" style={{ margin: 'auto' }} />
      </div>
    );
  }

  const hasAccess = subscription?.hasAccess;
  const trialDays = config?.trialDays ?? 30;
  const planAmount = config?.planAmount ?? 1000;
  const planDays = config?.planDays ?? 30;
  const upiId = config?.upiId ?? '';
  const upiQrUrl = config?.upiQrUrl ?? '';

  if (hasAccess) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="auth-container">
      <div className="auth-background" />
      <div className="auth-content">
        <div className="auth-card" style={{ maxWidth: 520 }}>
          <div className="auth-header">
            <div className="auth-logo">
              <FiCreditCard />
            </div>
            <h1 className="auth-title">Subscribe to continue</h1>
            <p className="auth-subtitle">
              Your {trialDays}-day trial has ended. Pay once to unlock all features for {planDays} days.
            </p>
          </div>

          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f0f9ff', borderRadius: 'var(--radius-lg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <FiCheckCircle style={{ color: 'var(--primary-500)' }} />
              <strong>Plan: Rs. {planAmount.toLocaleString()} for {planDays} days</strong>
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', margin: 0 }}>
              Pay via UPI and then confirm below. We will activate your access within 24 hours.
            </p>
          </div>

          {upiId && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FiSmartphone /> UPI ID – Pay to this address
              </label>
              <div
                style={{
                  padding: '1rem',
                  background: '#f8fafc',
                  borderRadius: 'var(--radius-lg)',
                  fontFamily: 'monospace',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                }}
              >
                {upiId}
              </div>
            </div>
          )}

          {upiQrUrl && (
            <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              <p className="input-label">Or scan this QR code</p>
              <img
                src={upiQrUrl}
                alt="UPI QR Code"
                style={{ maxWidth: 200, height: 'auto', border: '1px solid #e2e8f0', borderRadius: 'var(--radius-lg)' }}
              />
            </div>
          )}

          {!upiId && !upiQrUrl && (
            <p style={{ color: 'var(--gray-600)', marginBottom: '1rem', fontSize: '0.875rem' }}>
              UPI details will be configured by the admin. You can still submit a payment request after paying.
            </p>
          )}

          <form onSubmit={handleRequestPayment}>
            <div className="input-group" style={{ marginBottom: '1rem' }}>
              <label className="input-label">Transaction / UTR number (optional)</label>
              <input
                type="text"
                className="input-field"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="e.g. 123456789012"
              />
            </div>
            <div className="input-group" style={{ marginBottom: '1rem' }}>
              <label className="input-label">Notes (optional)</label>
              <textarea
                className="input-field"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any message for the admin"
                rows={2}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting}
            >
              I've paid – Activate my subscription
            </Button>
          </form>

          <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--gray-500)', textAlign: 'center' }}>
            After payment, click the button above. We will verify and enable your account shortly.
          </p>

          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={async () => {
                const sub = await subscriptionService.getSubscription().catch(() => ({ data: null }));
                const payload = sub.data; // { hasAccess, trialStartsAt, ... }
                const hasAccessNow = payload?.hasAccess === true;
                if (hasAccessNow) {
                  await refreshSubscription();
                  setSubscription(payload);
                  toast.success('Subscription active! Redirecting...');
                  setTimeout(() => navigate('/dashboard', { replace: true }), 150);
                } else {
                  toast.info('Not activated yet. We will activate within 24 hours.');
                }
              }}
            >
              Check status
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
